import { Autocomplete, TextField } from '@mui/material';
import type { MeasurementsStats } from '@weft/api-definition';
import type { Meter } from '@weft/types';
import { extract, isEmptyArray, withDefault } from '@weft/utils';
import React, { useMemo } from 'react';
import { VyRangePicker } from '../../../../components/new-design/inputs/VyRangePicker';
import { VyTableToolbar } from '../../../../components/new-design/table/vy-table-toolbar/vy-table-toolbar';
import { useAllCustomers } from '../../../../hooks/requests/use-customers';
import { useMeters } from '../../../../hooks/requests/use-meters';
import { useFromDayParam } from '../../../../hooks/search-params/use-from-day-param';
import { useToDayParam } from '../../../../hooks/search-params/use-to-day-param';
import { useStateParams } from '../../../../hooks/use-state-params';
import { deserializeStringArrayParam,
  serializeStringArrayParam } from '../../../../utils/params-formatting';
import { useEventNameMeters } from '../data-metering-types/data-metering-select-filters';
import { DataMeteringMenu } from './DataMeteringMenu';
import { ExportToCsvButton } from './ExportToCsvButton';

type DataMeteringFiltersContentProps = {
  measurementsStats: MeasurementsStats[];
  eventNames: string[];
};

export const DataMeteringFiltersContent: React.FC<
DataMeteringFiltersContentProps
> = ({ measurementsStats, eventNames }) => {
  const {
    data: { customers },
  } = useAllCustomers();
  const {
    data: { meters },
  } = useMeters();

  const distinctEventNames = useMemo(
    () => getDistinctEventNames(meters),
    [meters],
  );
  const filterMeters = useEventNameMeters(eventNames, meters);

  const [paramsFrom] = useFromDayParam();
  const [paramsTo] = useToDayParam();

  const startTime = paramsFrom?.toDate();
  const endTime = paramsTo?.toDate();

  const [selectedCustomersIds, setCustomersIds] = useStateParams<string[]>(
    [],
    'customers',
    serializeStringArrayParam,
    deserializeStringArrayParam,
  );
  const [selectedEventNames, setSelectedEventNames] = useStateParams<string[]>(
    [],
    'eventNames',
    serializeStringArrayParam,
    deserializeStringArrayParam,
  );
  const [selectedMetersIds, setSelectedMetersIds] = useStateParams<string[]>(
    [],
    'meters',
    serializeStringArrayParam,
    deserializeStringArrayParam,
  );

  return (
    <VyTableToolbar padding="24px 16px">
      <VyTableToolbar.Queries
        sx={{
          alignItems: 'start',
        }}
      >
        <VyRangePicker />

        <div>
          <Autocomplete
            multiple
            size="small"
            limitTags={1}
            options={customers}
            getOptionLabel={(customer) => customer.name}
            onChange={(_, value) =>
              setCustomersIds(value.map((customer) => customer.id))
            }
            value={customers.filter((customer) =>
              withDefault(selectedCustomersIds, []).includes(customer.id),
            )}
            sx={{
              width: '207px',
              minWidth: '100px',

            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Customers"
                fullWidth
              />
            )}
            disableClearable
          />
        </div>

        <Autocomplete
          multiple
          size="small"
          limitTags={1}
          options={distinctEventNames}
          onChange={(_, value) => setSelectedEventNames(value)}
          value={withDefault(selectedEventNames, [])}
          sx={{
            width: '207px',
            minWidth: '100px',
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Products"
              fullWidth
            />
          )}
          disableClearable
        />

        <Autocomplete
          multiple
          size="small"
          limitTags={1}
          options={filterMeters}
          getOptionLabel={(meterId) => meterId.name}
          onChange={(_, value) =>
            setSelectedMetersIds(value.map((meter) => meter.id))
          }
          value={filterMeters.filter((meter) =>
            withDefault(selectedMetersIds, []).includes(meter.id),
          )}
          sx={{
            width: '207px',
            minWidth: '100px',

          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Variants"
              fullWidth
            />
          )}
          disableClearable
        />
      </VyTableToolbar.Queries>

      <VyTableToolbar.Actions>
        <ExportToCsvButton
          disabled={isEmptyArray(measurementsStats)}
          endDay={startTime!}
          startDay={endTime!}
          measurementsStats={measurementsStats}
        />
        
        <DataMeteringMenu />
      </VyTableToolbar.Actions>
    </VyTableToolbar>
  );
};

function getDistinctEventNames(meters: Meter[]) {
  const meterIds = extract(meters, 'eventName');
  const meterIdsSet = new Set(meterIds);

  return [...meterIdsSet];
}
